import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@repo/ui";
import { Protected, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useEffect, useMemo, useState } from "react";

export default function ContactsPage() {
  return (
    <Protected redirectTo="/login">
      <Content />
    </Protected>
  );
}

function Content() {
  const authFetch = useAuthenticatedFetch();
  const [q, setQ] = useState("");
  type Contact = {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    title?: string;
    notes?: string;
  };
  const [data, setData] = useState<Contact[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  const load = async () => {
    const res = await authFetch(`/api/contacts?q=${encodeURIComponent(q)}`);
    setData((await res.json()) as Contact[]);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {
      name: form.get("name"),
      email: form.get("email"),
      phone: form.get("phone"),
      title: form.get("title"),
      notes: form.get("notes"),
    };
    if (editing?.id) {
      await authFetch(`/api/contacts/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    } else {
      await authFetch(`/api/contacts`, { method: "POST", body: JSON.stringify(payload) });
    }
    setOpen(false);
    setEditing(null);
    await load();
  };

  const startEdit = (row: Contact) => {
    setEditing(row);
    setOpen(true);
  };

  const remove = async (id: number) => {
    if (!confirm("Delete contact?")) return;
    await authFetch(`/api/contacts/${id}`, { method: "DELETE" });
    await load();
  };

  const rows = useMemo<Contact[]>(() => data, [data]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search contacts"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(null)}>New Contact</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Contact" : "New Contact"}</DialogTitle>
            </DialogHeader>
            <form className="space-y-3" onSubmit={onSubmit}>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" name="name" defaultValue={editing?.name || ""} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" defaultValue={editing?.email || ""} />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" name="phone" defaultValue={editing?.phone || ""} />
                </div>
              </div>
              <div>
                <Label htmlFor="title">Title/Role</Label>
                <Input id="title" name="title" defaultValue={editing?.title || ""} />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" name="notes" defaultValue={editing?.notes || ""} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <Button asChild variant="outline">
          <a href="/api/export/contacts.csv">Export</a>
        </Button>
      </div>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell>{r.phone}</TableCell>
                <TableCell>{r.title}</TableCell>
                <TableCell className="text-right space-x-2">
                  <NotesButton entityType="contact" entityId={r.id} />
                  <Button size="sm" variant="outline" onClick={() => startEdit(r)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function NotesButton({
  entityType,
  entityId,
}: {
  entityType: "contact" | "lead";
  entityId: number;
}) {
  const authFetch = useAuthenticatedFetch();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Array<{ id: number; content: string; created_at: string }>>(
    []
  );
  const [content, setContent] = useState("");

  const load = async () => {
    const res = await authFetch(`/api/notes?entityType=${entityType}&entityId=${entityId}`);
    const json = (await res.json()) as Array<{ id: number; content: string; created_at: string }>;
    setItems(json);
  };

  const add = async () => {
    if (!content.trim()) return;
    await authFetch(`/api/notes`, {
      method: "POST",
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId, content }),
    });
    setContent("");
    await load();
  };

  const remove = async (id: number) => {
    await authFetch(`/api/notes/${id}`, { method: "DELETE" });
    await load();
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Notes
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Notes</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {items.map((n) => (
            <div key={n.id} className="flex items-start justify-between gap-3 border rounded p-2">
              <div>
                <div className="text-sm whitespace-pre-wrap">{n.content}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(n.id)}>
                Delete
              </Button>
            </div>
          ))}
          <div className="space-y-2">
            <Label htmlFor={`note-${entityType}-${entityId}`}>Add note</Label>
            <Textarea
              id={`note-${entityType}-${entityId}`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className="flex justify-end">
              <Button onClick={add}>Add</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
